import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Navigation } from '@/components/layout/Navigation';
import { MainDashboard } from '@/components/dashboard/MainDashboard';
import { Shield, Briefcase, HeadphonesIcon } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { StrategyDashboard } from '@/components/trading/StrategyDashboard';
import { TradingHistory } from '@/components/trading/TradingHistory';
import { Sidebar } from '@/components/layout/Sidebar';
import { generateChatResponse } from '@/services/llmService';
import { ChatMessage } from '@/store';
import { useUpload } from '@/contexts/UploadContext';

const Index = () => {
  const navigate = useNavigate();
  const [selectedStrategy, setSelectedStrategy] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeView, setActiveView] = useState('chat');
  const [isExpanded, setIsExpanded] = useState(false);
  const [showRoles, setShowRoles] = useState(false);
  const [activeRole, setActiveRole] = useState(null);
  const { toast } = useToast();
  const { uploadedFiles, clearUploadedFiles } = useUpload();

  const [strategies, setStrategies] = useState<any[]>([]);
  useEffect(() => {
    async function loadStrategies() {
      try {
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) throw sessionError;
        const user = sessionData.session?.user;
        if (!user) return;
        const { data, error } = await supabase
          .from('strategies')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
        if (error) throw error;
        setStrategies(data || []);
      } catch (err) {
        console.error('Error loading strategies:', err);
      }
    }
    loadStrategies();
  }, []);
  const [positions, setPositions] = useState({
    overview: {
      totalValue: 0,
      todayChange: 0,
      totalPositions: 0
    },
    allocation: []
  });

  const [riskAlerts, setRiskAlerts] = useState([]);
  // Chat persistence: track conversation and load existing if any
  const [conversationId, setConversationId] = useState<string | null>(null);
  const location = useLocation();

  const roles = [
    {
      id: 'analyst',
      title: 'Financial Analyst',
      icon: Briefcase,
      description: 'Expert market analysis and portfolio recommendations',
      color: 'blue'
    },
    {
      id: 'compliance',
      title: 'Compliance Manager',
      icon: Shield,
      description: 'Regulatory oversight and risk management',
      color: 'green'
    },
    {
      id: 'concierge',
      title: 'E-Concierge',
      icon: HeadphonesIcon,
      description: 'Personalized support and guidance',
      color: 'purple'
    }
  ];

  const handleNewStrategy = async (data) => {
    // Persist new strategy in Supabase
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      const user = sessionData.session?.user;
      if (user) {
        const insertPayload = {
          user_id: user.id,
          name: data.name,
          description: `${data.engine} - ${data.riskLevel} risk`,
          engine: data.engine,
          risk_level: data.riskLevel,
          symbols: typeof data.symbols === 'string' ? data.symbols : JSON.stringify(data.symbols),
          status: 'active'
        };
        const { data: inserted, error: insertError } = await supabase
          .from('strategies')
          .insert([insertPayload])
          .select()
          .single();
        if (insertError) throw insertError;
        setStrategies(prev => [...prev, inserted]);
        setSelectedStrategy(inserted.id);
        toast({ title: "Strategy Created", description: `Created strategy: ${inserted.name}`, variant: "success" });
        return;
      }
    } catch (err: any) {
      console.error('Error saving strategy:', err);
      toast({ title: "Error", description: err.message || "Failed to save strategy", variant: "destructive" });
    }
    // Fallback to local state
    const newStrategy = {
      id: Date.now().toString(),
      name: data.name,
      description: `${data.engine} - ${data.riskLevel} risk`,
      isActive: true,
      engine: data.engine,
      riskLevel: data.riskLevel,
      duration: data.duration,
      symbols: data.symbols,
      profitTarget: data.profitTarget,
      stopLoss: data.stopLoss,
      isDynamicRisk: data.isDynamicRisk
    };
    
    const updatedStrategies = [...strategies, newStrategy];
    setStrategies(updatedStrategies);
    setSelectedStrategy(newStrategy.id);
    
    toast({
      title: "Strategy Created",
      description: `Created strategy: ${data.name}`,
      variant: "success",
    });
  };

  // Add strategy deletion handler
  const handleDeleteStrategy = (strategyId: string) => {
    console.log('Index: Deleting strategy from local state:', strategyId);
    
    // Remove strategy from local state
    setStrategies(prev => {
      const updated = prev.filter(strategy => strategy.id !== strategyId);
      console.log('Index: Updated strategies count:', updated.length);
      return updated;
    });
    
    // Clear selected strategy if it was the deleted one
    if (selectedStrategy === strategyId) {
      setSelectedStrategy('');
    }
    
    console.log('Index: Strategy deletion completed');
  };

  const handleTestCompleted = (testResult) => {
    if (testResult.performance.totalReturn > 0) {
      const newStrategy = {
        id: Date.now().toString(),
        name: `${testResult.name} Strategy`,
        description: `Created from ${testResult.type} test with ${testResult.performance.totalReturn.toFixed(2)}% return`,
        isActive: true,
        engine: 'Stewie AI',
        riskLevel: 'moderate',
        duration: '1D',
        symbols: testResult.positions.map(p => p.symbol).join(','),
        isDynamicRisk: true
      };
      
      setStrategies(prev => [...prev, newStrategy]);
      setSelectedStrategy(newStrategy.id);
      
      if (strategies.length === 0) {
        const testPositions = testResult.positions.reduce((total, pos) => total + (pos.quantity * pos.currentPrice), 0);
        
        setPositions({
          overview: {
            totalValue: testPositions,
            todayChange: testResult.performance.totalReturn,
            totalPositions: testResult.positions.length
          },
          allocation: [
            { 
              type: 'US Equities', 
              value: 100, 
              amount: testPositions 
            }
          ]
        });
      }
      
      toast({
        title: "Strategy Created",
        description: `A new strategy has been created based on your successful ${testResult.type} test.`,
        variant: "success",
      });
    } else {
      toast({
        title: "Test Completed",
        description: `The ${testResult.type} test completed with a negative return of ${testResult.performance.totalReturn.toFixed(2)}%.`,
        variant: "warning",
      });
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;
    
    const currentInput = inputMessage;
    setInputMessage('');
    setIsLoading(true);
    let convId = conversationId;

    try {
      // Create conversation if it doesn't exist
      if (!convId) {
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        if (sessionError || !sessionData.session?.user) {
          throw new Error(sessionError?.message || 'User not authenticated');
        }
        const titleSnippet = currentInput.length > 50
          ? currentInput.substring(0, 50) + '...'
          : currentInput;
        const { data: newConv, error: convError } = await supabase
          .from('conversations')
          .insert({ user_id: sessionData.session.user.id, title: titleSnippet, context_type: 'chat' })
          .select()
          .single();
        if (convError || !newConv?.id) {
          throw new Error(convError?.message || 'Failed to create conversation');
        }
        convId = newConv.id;
        setConversationId(convId);
      }

      // Save user message - fixing the field name from 'role' to 'sender'
      await supabase.from('chat_messages').insert({ 
        conversation_id: convId, 
        sender: 'user', 
        content: currentInput,
        user_id: (await supabase.auth.getSession()).data.session?.user?.id
      });
      
      const userMessage: ChatMessage = {
        id: `${Date.now().toString()}-user`,
        role: 'user',
        content: currentInput,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, userMessage]);

      // Prepare history including this new message
      const historyForAPI = [...messages, userMessage].map(msg => ({
        role: msg.role,
        content: msg.content,
      }));

      // Detect if message contains financial context
      const { detectFinancialContext, enhancedFinancialResponse } = await import('@/services/chatService');
      const { isFinancial, symbols } = detectFinancialContext(currentInput);
      
      let aiContent = '';
      let aiMetadata = null;
      
      // If financial content is detected and we have symbols, use the financial LLM
      // But respect manual role selection over automatic detection
      if (isFinancial && symbols.length > 0 && !activeRole) {
        try {
          console.log('Using financial LLM with symbols:', symbols.join(', '));
          const financialResponse = await enhancedFinancialResponse(currentInput, symbols);
          
          if (financialResponse.success && financialResponse.response) {
            aiContent = financialResponse.response;
            aiMetadata = {
              model_used: financialResponse.model_used || 'Financial LLM',
              rag_sources_used: financialResponse.rag_sources_used || ['Alpaca market data']
            };
                            } else {
            // If financial LLM fails, fall back to standard LLM with role
            console.log('Financial LLM failed, falling back to standard LLM');
            aiContent = await generateChatResponse(currentInput, historyForAPI, undefined, activeRole, uploadedFiles);
          }
        } catch (finError) {
          console.error('Error using financial LLM:', finError);
          aiContent = await generateChatResponse(currentInput, historyForAPI, undefined, activeRole, uploadedFiles);
        }
      } else {
        // Use role-based chat response (manual role selection or non-financial queries)
        console.log('Using role-based LLM with role:', activeRole || 'default');
        aiContent = await generateChatResponse(currentInput, historyForAPI, undefined, activeRole, uploadedFiles);
      }

      // Save AI response - fixing the field name from 'role' to 'sender'
      await supabase.from('chat_messages').insert({ 
        conversation_id: convId, 
        sender: 'assistant', 
        content: aiContent,
        user_id: (await supabase.auth.getSession()).data.session?.user?.id,
        metadata: aiMetadata // Store metadata in the database if available
      });
      
      const aiMessage: ChatMessage = {
        id: `${Date.now().toString()}-assistant`,
        role: 'assistant',
        content: aiContent,
        timestamp: new Date(),
        metadata: aiMetadata // Add metadata to message for display
      };
      setMessages(prev => [...prev, aiMessage]);

    } catch (error: any) {
      console.error('Error in sending message:', error);
      const errorMessage: ChatMessage = {
        id: `${Date.now().toString()}-error`,
        role: 'assistant',
        content: `Sorry, I encountered an error: ${error.message || 'Please try again.'}`,
        timestamp: new Date(),
        error: true,
      };
      setMessages(prev => [...prev, errorMessage]);
      toast({ title: 'Error', description: error.message || 'An error occurred while sending your message.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
      // Clear uploaded files after message processing (success or failure) to prevent memory leaks
      if (uploadedFiles.length > 0) {
        clearUploadedFiles();
      }
    }
  };

  useEffect(() => {
    const checkUserAccess = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user) {
        // Fetch user role with error handling
        const { data: roleData, error: roleError } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', session.user.id)
          .maybeSingle();
        if (roleError) {
          console.error('Error fetching user role:', roleError);
        } else if (roleData?.role === 'compliance_manager' && activeView === 'trader') {
          setActiveView('chat');
          toast({
            title: "Access Restricted",
            description: "RIA clients only have access to Stewie Chat.",
            variant: "destructive",
          });
        }

        const params = new URLSearchParams(window.location.search);
        const viewParam = params.get('view');
        if (viewParam && (viewParam === 'chat' || viewParam === 'trader')) {
          setActiveView(viewParam);
        }
      }
    };

    checkUserAccess();
  }, []);
  // React to navigation state changes: load or clear selected conversation
  const navState = (location.state as { activeView?: string; conversationId?: string }) || {};
  useEffect(() => {
    if (navState.activeView === 'chat' || navState.activeView === 'trader') {
      setActiveView(navState.activeView);
    }

    if (!navState.conversationId) {
      // No conversation selected: clear messages
      setConversationId(null);
      setMessages([]);
      return;
    }

    // Load the selected conversation's messages
    setConversationId(navState.conversationId);
    (async () => {
      try {
        const { data: chatData, error: chatError } = await supabase
          .from('chat_messages')
          .select('id, sender, content, timestamp')
          .eq('conversation_id', navState.conversationId)
          .order('timestamp', { ascending: true });

        if (chatError) throw chatError;
        const loaded: ChatMessage[] = (chatData || []).map((msg: any) => ({
          id: msg.id,
          role: msg.sender as 'user' | 'assistant',
          content: msg.content,
          timestamp: new Date(msg.timestamp),
        }));

        setMessages(loaded);
      } catch (error: any) {
        toast({ title: 'Error loading conversation', description: error.message, variant: 'destructive' });
      }
    })();
  }, [navState.conversationId, navState.activeView]);

  const renderContent = () => {
    const pathname = window.location.pathname;
    
    if (pathname === '/trading-history') {
      return <TradingHistory />;
    }
    
    return (
      <>
        <Navigation 
          activeView={activeView}
          setActiveView={setActiveView}
          showRoles={showRoles}
          setShowRoles={setShowRoles}
        />

        <div className="flex">
          <Sidebar className="hidden md:block" />
          
          <MainDashboard 
            activeView={activeView}
            messages={messages}
            inputMessage={inputMessage}
            setInputMessage={setInputMessage}
            isLoading={isLoading}
            handleSendMessage={handleSendMessage}
            isExpanded={isExpanded}
            setIsExpanded={setIsExpanded}
            showRoles={showRoles}
            setShowRoles={setShowRoles}
            activeRole={activeRole}
            setActiveRole={setActiveRole}
            roles={roles}
            selectedStrategy={selectedStrategy}
            setSelectedStrategy={setSelectedStrategy}
            positions={positions}
            strategies={strategies}
            handleNewStrategy={handleNewStrategy}
            handleDeleteStrategy={handleDeleteStrategy}
            riskAlerts={riskAlerts}
          />
        </div>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {renderContent()}
    </div>
  );
};

export default Index;
